// @ts-nocheck
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { calculateTrustScore } from '@/utils/trustScore';
import { TRUST_SCORE_FORMULA } from '@/constants/legal';

interface TrustScoreDialogProps {
  isOpen: boolean;
  onClose: () => void;
  trustScore: number;
  userName?: string;
  firstDegreeCount?: number;
  secondDegreeCount?: number;
  isVerified?: boolean; // V177.VERIFY: Add verification status
  /** Discover uses connectionScore; Profile keeps trustScore (default). */
  titleVariant?: 'trustScore' | 'connectionScore';
}

export const TrustScoreDialog: React.FC<TrustScoreDialogProps> = React.memo(({
  isOpen,
  onClose,
  trustScore,
  firstDegreeCount = 0,
  secondDegreeCount = 0,
  isVerified = false, // V177.VERIFY: Default to unverified
  titleVariant = 'trustScore',
}) => {
  const { t } = useTranslation();

  const titleKey =
    titleVariant === 'connectionScore'
      ? 'trustScore.connectionScoreTitle'
      : 'trustScore.title';

  // 🚨 V131.7 EXCELLENCE: Enhanced keyboard navigation and focus management
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Focus management for accessibility
    const focusButton = () => {
      const button = document.querySelector('[data-trust-score-button]') as HTMLElement;
      if (button) {
        button.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    setTimeout(focusButton, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // V291.3: Calculate trust score using unified helper (single source of truth)
  // Formula: (friends × 10) + (2nd degree × 1) + (verified × 100)
  const displayTrustScore = useMemo(() => {
    return calculateTrustScore({
      firstDegreeCount: firstDegreeCount,
      secondDegreeCount: secondDegreeCount,
      isVerified,
    });
  }, [firstDegreeCount, secondDegreeCount, isVerified]);

  const verifiedBonus = TRUST_SCORE_FORMULA.VERIFICATION_BONUS;
  const firstDegreeMultiplier = TRUST_SCORE_FORMULA.DIRECT_FRIEND_POINTS;
  const secondDegreeMultiplier = TRUST_SCORE_FORMULA.SECOND_DEGREE_POINTS;

  // 🚨 V131.8 CLEAN: Simplified component without complex memoization

  if (!isOpen) return null;
  return (
    // V291.3.1: Increased z-index to z-[1200] to ensure dialog is always above bottom nav (z-40/z-60)
    <div className="fixed inset-0 z-[1200] flex items-end justify-center">
      {/* Clean backdrop without blur */}
      <div
        className="absolute inset-0 bg-black/20"
        onClick={onClose}
      />

      {/* 
        V291.3.1: Bottom-positioned modal with proper navigation clearance
        Uses CSS calc to add bottom-nav-height + safe-area-bottom + 12px buffer
        This ensures the dialog content is never covered by the bottom tab bar
      */}
      {/* 
        V291.4: Mobile-optimized dialog positioning
        - marginBottom: clears bottom nav + safe area + 24px buffer
        - maxHeight: prevents overflow above bottom nav
        - overflow-y-auto: allows scrolling if content exceeds available space
      */}
      {/* 
        V291.5: Android WebView fix - use explicit bottom offset instead of marginBottom
        - bottom: anchors dialog above bottom nav reliably on Android
        - 100dvh: dynamic viewport height respects Android browser UI changes
        - Fallback to 100vh for older browsers via CSS custom property
      */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="trust-score-title"
        data-trust-modal
        className="absolute left-0 right-0 bg-white dark:bg-gray-900 w-full max-w-sm mx-auto rounded-t-2xl shadow-xl animate-in slide-in-from-bottom duration-300 overflow-y-auto"
        style={{
          bottom: 'calc(var(--bottom-nav-height, 72px) + var(--sab, 0px) + 16px)',
          maxHeight: 'calc(100dvh - var(--bottom-nav-height, 72px) - var(--sab, 0px) - 48px)'
        }}
      >
        {/* Top handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-8 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
        </div>

        {/* Enhanced header */}
        <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
          <h2 id="trust-score-title" className="text-lg font-semibold text-center text-gray-900 dark:text-gray-100">
            {t(titleKey)}
          </h2>
        </div>

        {/* Enhanced content */}
        <div className="px-4 py-4 space-y-4">
          {/* Trust score display — number only; title is in the header */}
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {displayTrustScore}
            </div>
          </div>

          {/* ENHANCED: Large, prominent formula - V180.4.4: Fixed light mode styling */}
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 p-4 rounded-xl border border-gray-300 dark:border-gray-600/30">
            <div className="text-start text-gray-600 dark:text-gray-300 text-xs mb-3 uppercase tracking-wide font-medium">
              {t('trustScore.calculation')}
            </div>

            {/* Math only — forced LTR so × + () render correctly in RTL locales */}
            <div className="font-mono text-base font-bold text-gray-900 dark:text-gray-100 text-center" dir="ltr">
              {'('}
              <span className="text-green-600 dark:text-green-400">{firstDegreeCount}</span>
              {' × '}
              <span className="text-blue-600 dark:text-blue-400">{firstDegreeMultiplier}</span>
              {')'}
              <span className="text-gray-600 dark:text-gray-400"> + </span>
              {'('}
              <span className="text-yellow-600 dark:text-yellow-400">{secondDegreeCount}</span>
              {' × '}
              <span className="text-blue-600 dark:text-blue-400">{secondDegreeMultiplier}</span>
              {')'}
              {isVerified && (
                <>
                  <span className="text-gray-600 dark:text-gray-400"> + </span>
                  <span className="text-purple-600 dark:text-purple-400">{verifiedBonus}</span>
                </>
              )}
            </div>

            {/* Labels outside LTR wrapper — text-start for RTL Hebrew */}
            <div className="mt-3 space-y-1 text-xs text-start text-gray-600 dark:text-gray-400 break-words">
              <div>{t('trustScore.firstDegreeBreakdown', { count: firstDegreeCount })}</div>
              <div>{t('trustScore.secondDegreeBreakdown', { count: secondDegreeCount })}</div>
              {isVerified && (
                <div>{t('trustScore.verifiedBreakdown', { bonus: verifiedBonus })}</div>
              )}
            </div>

            <div className="mt-4 border-t border-gray-300 dark:border-gray-600 pt-3 text-start font-semibold text-gray-900 dark:text-white">
              {t('trustScore.totalLabel', { score: displayTrustScore })}
            </div>
          </div>

          {/* UPDATED: Enhanced encouragement */}
          {displayTrustScore === 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 p-3 rounded-xl text-center">
              <p className="text-blue-800 dark:text-blue-300 text-sm font-medium">
                {t('trustScore.inviteMoreFriends')}
              </p>
            </div>
          )}

          {/* Enhanced close button */}
          <button
            data-trust-score-button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-xl transition-all duration-200"
          >
            {t('trustScore.gotIt')}
          </button>
        </div>
      </div>
    </div>
  );
});

export default TrustScoreDialog;
